import '../../styles/InteractiveComponents.css';
import '../../styles/FreeText.css';

import {
  FreeTextComponent,
  LessonComponentResponseType
} from '../../../content/models/LessonComponent';
import {
  LessonComponentContextConsumer,
  useLessonComponentContext
} from '../../models/LessonComponentContext';
import React, { useEffect, useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { isNull, isUndefined } from 'lodash';

import Box from '@mui/material/Box';
import Constants from '../../constants';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Unstable_Grid2';
import { InlineQuillEdit } from '../InlineQuillEdit';
import LessonComponentService from '../../services/LessonComponentService';
import WarningIcon from '@mui/icons-material/Warning';
import componentStyles from '../../styles/LessonComponents.module.css';
import { useLessonContext } from '../../models/LessonContext';

interface FreeTextProps {
  response: LessonComponentResponseType;
  component: FreeTextComponent;
  onChange: (response: LessonComponentResponseType) => void;
}

export function FreeText(props: FreeTextProps) {
  const { mode, finalized, quizLocked, assessmentSubmitted } =
    useLessonContext();
  const { answerSaved } = useLessonComponentContext();
  const [response, setResponse] = useState<string>(
    (props.response as string) || ''
  );

  const [charactersCount, setCharactersCount] = useState(0);

  useEffect(() => {
    if (!isUndefined(props.response)) {
      setResponse((props.response as string) || '');
      setCharactersCount(
        LessonComponentService.stripTags(props.response as string)?.length || 0
      );
    }
  }, [props.response]);

  function handleChange(value: string) {
    if (mode !== 'edit') {
      props.onChange(value);
      setResponse(value);
    }
  }

  const warning =
    charactersCount >= Constants.WarningFreeTextCharacters &&
    charactersCount < Constants.MaximumFreeTextCharacters;
  const maxLimit = charactersCount >= Constants.MaximumFreeTextCharacters;
  const warningText = `You still have ${
    Constants.MaximumFreeTextCharacters - (charactersCount || 0)
  } more characters`;
  const maxLimitText = `You have reached maximum limit.`;

  return (
    <LessonComponentContextConsumer>
      {({ correct, reattempt }) => (
        <Box className={componentStyles.textOverflow}>
          {/* Set width to work with safari browser */}
          <FormLabel component="legend" style={{ width: '100%' }}>
            <div
              className={mode === 'edit' ? 'disable-textbox' : 'answer-textbox'}
            >
              <InlineQuillEdit
                value={mode !== 'edit' ? response : ''}
                componentKey={props.component.componentkey}
                styleType="noList"
                disable={
                  mode === 'edit' ||
                  mode === 'mark' || //NOT editable for marker
                  (!isNull(correct) && !reattempt) ||
                  quizLocked ||
                  finalized ||
                  assessmentSubmitted
                }
                placeholder={props.component.settings.instructionText}
                onCharactersCountChange={setCharactersCount}
                onChange={handleChange}
              />
            </div>
          </FormLabel>
          {(((mode === 'learn' || mode === 'preview') && !answerSaved) ||
            reattempt) && (
            <Grid container alignItems="center">
              <Grid>
                <Typography variant="body2">
                  <span
                    style={{
                      color: warning ? 'orange' : maxLimit ? 'red' : 'inherit'
                    }}
                  >
                    {charactersCount}
                  </span>{' '}
                  of {Constants.MaximumFreeTextCharacters} characters
                </Typography>
              </Grid>
              {(warning || maxLimit) && (
                <Grid pl="10px" height="20px">
                  <Tooltip title={warning ? warningText : maxLimitText}>
                    <WarningIcon
                      fontSize="small"
                      color={warning ? 'warning' : 'error'}
                    />
                  </Tooltip>
                </Grid>
              )}
            </Grid>
          )}
        </Box>
      )}
    </LessonComponentContextConsumer>
  );
}
